// CompetitionClassRegistrationsDatatable.js

import { DotsHorizontalIcon } from '@radix-ui/react-icons';
import {
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import '../../App.css';
import { AuthContext } from '../../context/AuthContext';
import AddAdvancementpointDialog from '../dialogs/addAdvancementpointDialog';
import AddCertificationDialog from '../dialogs/addCertificateDialog';
import ChangeBunnyClassDialog from '../dialogs/changeBunnyClassDialog';
import ChangeDriverDialog from '../dialogs/changeDriverDialog';
import EditRegistrationDialog from '../dialogs/editRegistrationDialog';
import { SkeletonRow } from '../skeletons/skeletonrow';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog } from "../ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../ui/table";
import DatatableSortButton from './datatableSortButton';

export const columns = (fetchClass, auth, competitionClass, setSelectedBunny, setDialogOpen, forCup, dataLength) => {
    const isAdminOrPrivilegedUser = auth?.user?.superadmin || (auth?.user?.roleInClub === 'admin' && auth?.user?.memberOfClub === parseInt(competitionClass?.competition?.club?.id)) || auth?.user?.judge || auth?.user?.bunnyadmin;

    const baseColumns = [
        {
            accessorKey: "bunnyName",
            accessorFn: (row) => row.bunny?.name, // extract the nested name
            header: ({ column }) => (
                <DatatableSortButton column={column}>Kanin</DatatableSortButton>
            ),
            cell: info => (
                <Link
                    to={`/bunny/${info?.row?.original?.bunny?.id}`}
                    className='text-blue-600 font-medium hover:underline'
                >
                    {info?.row?.original?.bunny?.name}
                </Link>
            ),
        },
        {
            accessorKey: "driver_name",
            accessorFn: (row) => row.driver?.first_name, // extract the nested first name
            header: ({ column }) => (
                <DatatableSortButton column={column}>Förare</DatatableSortButton>
            ),
            cell: info => {
                const registration = info.row.original;
                return `${registration.driver?.first_name} ${registration.driver?.last_name}`;
            },
        },
    ]

    if (!forCup) {
        baseColumns.unshift(
            {
                accessorKey: "startNumber",
                header: ({ column }) => (
                    <DatatableSortButton column={column}>Startnummer</DatatableSortButton>
                ),
                sortingFn: (rowA, rowB, columnId) => {
                    const a = rowA.getValue(columnId);
                    const b = rowB.getValue(columnId);

                    // Handle undefined or null values (place them at the bottom)
                    if (a === null) return 1; // `a` is undefined/null, so it should come after `b`
                    if (b === null) return -1; // `b` is undefined/null, so it should come after `a`

                    // Perform a numeric comparison if both are defined
                    return a - b;
                },
                cell: info => {
                    const bunny = info.row.original || {}; // Safely access bunny
                    const startNumber = info.row.original.startNumber || ''; // Safely get startNumber
                    return (
                        <div className='flex items-center'>
                            {startNumber &&
                                <span className='mr-3'>{startNumber}</span>
                            }
                            {bunny.crossedOut ? (
                                <Badge className='text-nowrap' variant='destructive'>Struken</Badge>
                            ) : bunny.outOfCompetition ? (
                                <Badge className='text-nowrap' variant='secondary'>Utom tävlan</Badge>
                            ) : bunny.only_cup ? (
                                <Badge className='text-nowrap' variant='outline'>Endast cup</Badge>
                            ) : null}
                        </div>
                    );
                },
            },
        );
        baseColumns.push(
            {
                accessorKey: "comment",
                header: ({ column }) => (
                    <DatatableSortButton column={column}>Kommentar</DatatableSortButton>
                ),
                cell: info => {
                    const registration = info.row.original;
                    return registration.comment;
                },
            },
        )
    }

    if (isAdminOrPrivilegedUser && dataLength > 0) {
        baseColumns.push(
            {
                id: "actions",
                enableHiding: false,
                cell: ({ row }) => {
                    const registration = row.original;
                    return (
                        <Dialog modal={true}>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="h-8 w-8 p-0 float-right">
                                        <span className="sr-only">Öppna meny</span>
                                        <DotsHorizontalIcon className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    align="end"
                                    onCloseAutoFocus={(e) => {
                                        e.preventDefault();
                                    }}
                                >
                                    <DropdownMenuLabel>Alternativ</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    {!forCup && (
                                        <EditRegistrationDialog registration={registration} fetchClass={fetchClass} dropdown={true} cup={competitionClass?.cup} />
                                    )}
                                    {/* <AddRegistrationCommentDialog registration={registration} fetchClass={fetchClass} useApi={true} dropdown={true} /> */}
                                    {(![17, 14, 11, 5].includes(competitionClass.sport_class_id) && !forCup) && (
                                        <AddAdvancementpointDialog fetchQualifications={fetchClass} bunny={registration.bunny} dropdown={true} competitionClass={competitionClass} />
                                    )}
                                    {([17, 14, 11, 5].includes(competitionClass.sport_class_id) && !forCup) && (
                                        <AddCertificationDialog fetchQualifications={fetchClass} bunny={registration.bunny} dropdown={true} competitionClass={competitionClass} />
                                    )}
                                    {auth?.user?.bunnyadmin || auth?.user?.superadmin &&
                                        <DropdownMenuItem
                                            onClick={() => {
                                                setSelectedBunny(registration?.bunny?.id);
                                                setDialogOpen(true);
                                            }}
                                            className='cursor-pointer'
                                        >
                                            Ändra klasser
                                        </DropdownMenuItem>
                                    }
                                    {(!registration.crossedOut && !forCup) && (
                                        <>
                                            <DropdownMenuSeparator />
                                            <ChangeDriverDialog registration={registration} fetchClass={fetchClass} />
                                            {/* <ChangeOutOfCompetitionDialog registration={registration} fetchClass={fetchClass} /> */}
                                        </>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </Dialog>
                    );
                },
            },
        )
    }
    return baseColumns;
};

export function CompetitionClassRegistrationsCollapsibleDatatable({ competitionClass, isLoading, fetchClass, participants, bunnies, forCup, setColumnVisibility, columnVisibility, onColumnsReady }) {
    const [columnFilters, setColumnFilters] = useState([]);
    const { auth } = useContext(AuthContext);
    const [sorting, setSorting] = useState(forCup ? [] : [{ id: "startNumber", desc: false }]);

    const [selectedBunny, setSelectedBunny] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    let data = []
    const memoizedData = useMemo(() => {
        if (forCup === true) {
            // Filter bunnies based on forCup flag
            const filteredBunnies = bunnies.flatMap(user =>
                user.bunnies.filter(bunny => {
                    if (!forCup) return true; // If forCup is false, include all bunnies

                    // Get all class IDs in this cup
                    const cupClassIds = competitionClass.classes.map(cls => cls.id);

                    // Find all registrations for this bunny
                    const bunnyRegistrations = participants.flatMap(participant =>
                        participant.registrations.filter(reg => reg.bunnyId === bunny.bunnyId)
                    );

                    // For each cup class, check if the bunny has a registration with inCup=true
                    const isInAllCupClasses = cupClassIds.every(classId => {
                        const registration = bunnyRegistrations.find(reg => reg.classId === classId);
                        return registration && registration.inCup;
                    });

                    return isInAllCupClasses;
                })
            );

            // Then map the filtered bunnies to the table data format
            return filteredBunnies.map(bunny => {
                // Find the participant who owns this bunny
                const participant = participants.find(p =>
                    p.registrations.some(reg => reg.bunnyId === bunny.bunnyId)
                );

                // Find all registrations for this bunny
                const bunnyRegistrations = participant?.registrations.filter(
                    reg => reg.bunnyId === bunny.bunnyId
                ) || [];

                // Get a cup registration for this bunny to extract startNumber and comment
                const cupRegistration = bunnyRegistrations.find(reg =>
                    competitionClass.classes.some(cls => cls.id === reg.classId) && reg.inCup
                ) || bunnyRegistrations[0] || {};

                return {
                    bunny: {
                        id: bunny.bunnyId,
                        name: bunny.bunnyName
                    },
                    driver: {
                        first_name: participant?.firstName || '',
                        last_name: participant?.lastName || ''
                    },
                    outOfCompetition: cupRegistration.outOfCompetition || false,
                    crossedOut: cupRegistration.crossedOut || false,
                    registrationId: cupRegistration.registrationId || '',
                    in_cup: cupRegistration.inCup || false,
                };
            });
        } else {
            return competitionClass.registrations
        }
    }, [forCup, competitionClass, bunnies, participants]);

    const columnsMemoized = useMemo(() => columns(fetchClass, auth, competitionClass, setSelectedBunny, setDialogOpen, forCup, memoizedData?.length), [fetchClass, auth, competitionClass, setSelectedBunny, setDialogOpen, forCup, memoizedData?.length]);

    useEffect(() => {
        if (onColumnsReady) {
            onColumnsReady(columnsMemoized);
        }
    }, [onColumnsReady]);

    const table = useReactTable({
        // data: forCup ? data : competitionClass.registrations,
        data: memoizedData,
        columns: columnsMemoized,
        getRowId: (row, index) => row.id || index.toString(),
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            pagination: {
                pageIndex: 0,
                pageSize: 9999,
            },
        },
    });

    return (
        <div>
            <ChangeBunnyClassDialog bunnyId={selectedBunny} isDialogOpen={dialogOpen} setIsDialogOpen={setDialogOpen} onlyMount={true} />
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        {table?.getHeaderGroups()?.map(headerGroup => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers?.map(header => (
                                    <TableHead
                                        style={{
                                            minWidth: header.column.columnDef.size,
                                            maxWidth: header.column.columnDef.size,
                                        }}
                                        key={header.id}
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <SkeletonRow columns={columns().length} />
                        ) : table.getRowModel().rows?.length ? (
                            table.getRowModel().rows?.map(row => (
                                <Fragment key={row.id}>
                                    <TableRow data-state={row.getIsSelected() && "selected"}>
                                        {row.getVisibleCells().map(cell => (
                                            <TableCell key={cell.id}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                    {/* Render each result as its own ExpandedContent row
                                    {row.original.straightCrookedResults &&
                                        [...row.original.straightCrookedResults]
                                            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                                            .map((result, index) => (
                                                <TableRow key={`sc-${result.id}-${index}`} className='bg-gray-100'>
                                                    <TableCell colSpan={columns().length}>
                                                        <ExpandedContentStraightCrooked result={result} round={index + 1} />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                    {row.original.heightLengthResults &&
                                        [...row.original.heightLengthResults]
                                            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                                            .map((result, index) => (
                                                <TableRow key={`hl-${result.id}-${index}`} className='bg-gray-100'>
                                                    <TableCell colSpan={columns().length}>
                                                        <ExpandedContentHeightLength result={result} round={index + 1} sportClassId={competitionClass.sportClassId} />
                                                    </TableCell>
                                                </TableRow>
                                            ))} */}
                                </Fragment>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns().length} className="h-24 text-center">
                                    Inget hittades.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            {/* Pagination disabled to match with the following datatables for the class. */}
            {/* <div className="flex items-center justify-end py-4 space-x-2">
                <div className="flex w-[100px] items-center justify-center text-sm font-medium">
                    Sida {table.getState().pagination.pageIndex + 1} av {table.getPageCount()}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                >
                    Föregående
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                >
                    Nästa
                </Button>
            </div> */}
        </div>
    );
}

// ExpandedContent component to display a single result
function ExpandedContentStraightCrooked({ result, round }) {
    const { errors, time, created_at } = result;

    return (
        <div className="bg-gray-100 pl-4 flex flex-row gap-4">
            <p><span className='font-medium'>Omgång {round}:</span></p>
            <p>Antal fel: {errors}</p>
            <p>Tid: {time} sekunder</p>
            <p>Registrerades: {new Date(created_at).toLocaleString()}</p>
        </div>
    );
}

// ExpandedContent component to display a single result
function ExpandedContentHeightLength({ result, round, sportClassId }) {
    const { height_length, attempts, points, rejump, created_at } = result;

    const isHeightClass = sportClassId >= 13 && sportClassId <= 15;

    return (
        <div className="bg-gray-100 pl-4 flex flex-row gap-4">
            <p><span className='font-medium'>Omgång {round}:</span></p>
            <p>{isHeightClass ? 'Höjd' : 'Längd'}: {height_length}cm</p>
            <p>Antal försök: {attempts}</p>
            <p>Poäng: {points}</p>
            {rejump ? (<p>Omhopp</p>) : null}
            <p>Registrerades: {new Date(created_at).toLocaleString()}</p>
        </div>
    );
}